import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { CardQuestion, CardActions } from "./CardPrimitives";
import { useIntakeMobileChromeRegistration } from "../IntakeMobileChromeContext";
import type { CardDef, UploadCategory } from "../types";
import { patientIntakeApi } from "@/lib/patientIntakeApi";

/** Routes a single dropped/selected file when using the combined upload zone. */
function routeFileToUploadCategory(file: File): UploadCategory {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  if (
    type === "application/pdf" ||
    type === "application/dicom" ||
    type === "image/tiff" ||
    name.endsWith(".pdf") ||
    name.endsWith(".dcm") ||
    /\.tiff?$/.test(name)
  ) {
    return "labsImaging";
  }
  if (type.startsWith("image/")) {
    return "photos";
  }
  return "labsImaging";
}
import type { IntakeData, UploadedMedia } from "@/lib/types";
import type { DocumentReferenceLink } from "@/lib/types";
import { Camera, FileText, Link, Loader2, Plus, Upload, X } from "lucide-react";
import ModernCameraCapture from "@/components/intake/ModernCameraCapture";

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageFile(file: File) {
  return file.type.startsWith("image/");
}

const isUploadedMedia = (item: File | UploadedMedia): item is UploadedMedia =>
  "isUploaded" in item && item.isUploaded === true;

const getUploadItemName = (item: File | UploadedMedia): string =>
  isUploadedMedia(item) ? item.filename : item.name;

function UploadFileRow({
  file,
  onRemove,
  uploadProgress,
  isUploading,
  uploadError,
  isRemoving,
}: {
  file: File | UploadedMedia;
  onRemove: () => void;
  uploadProgress?: number;
  isUploading?: boolean;
  uploadError?: string;
  isRemoving?: boolean;
}) {
  const { _ } = useLingui();
  const isUploaded = isUploadedMedia(file);
  const isLocalFile = file instanceof File;
  const fileName = isUploaded ? file.filename : file.name;
  const fileSize = isUploaded ? file.size : file.size;
  const mimeType = isUploaded ? file.mimeType : file.type;
  const previewUrl = useMemo(
    () => {
      if (isUploaded) {
        return mimeType.startsWith("image/") ? file.downloadUrl : null;
      }
      return isImageFile(file) ? URL.createObjectURL(file) : null;
    },
    [file, isUploaded, mimeType],
  );

  useEffect(() => {
    return () => {
      if (previewUrl && isLocalFile) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl, isLocalFile]);

  return (
    <li
      className={`flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-3.5 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${
        isRemoving ? "opacity-70" : ""
      }`}
    >
      {previewUrl ? (
        <div className="relative h-14 w-14 overflow-hidden rounded-xl border border-gray-200 bg-gray-100 ring-1 ring-black/5">
          <img src={previewUrl} alt={fileName} className="h-full w-full object-cover" />
          {isUploading && (
            <div className="absolute inset-0 bg-black/55" />
          )}
        </div>
      ) : (
        <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-gray-200 bg-gray-50">
          <FileText className="h-5 w-5 text-gray-400" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-700">{fileName}</p>
        <p className="mt-0.5 text-xs text-gray-400">
          {formatSize(fileSize)}
          {isUploading ? ` - ${uploadProgress ?? 0}%` : ""}
        </p>
        {isUploading && (
          <div className="mt-1.5 h-1.5 w-full rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-[#166B93] transition-all duration-200"
              style={{ width: `${Math.min(100, Math.max(0, uploadProgress ?? 0))}%` }}
            />
          </div>
        )}
        {uploadError && (
          <p className="mt-1 text-xs text-red-600">{uploadError}</p>
        )}
        {isRemoving && (
          <p className="mt-1 text-xs text-gray-500">
            <Trans>Removing file...</Trans>
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={isRemoving}
        className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
        aria-label={_(msg`Remove file`)}
      >
        {isRemoving ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <X className="h-4 w-4" />
        )}
      </button>
    </li>
  );
}

function UploadDropZone({
  onFilesAdded,
  showCameraCapture = false,
  onTakePhoto,
}: {
  onFilesAdded: (files: File[]) => void;
  showCameraCapture?: boolean;
  onTakePhoto?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length) onFilesAdded(dropped);
  };

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={`rounded-2xl border-2 border-dashed p-5 text-center transition-all duration-150 sm:p-6 ${
        isDragging
          ? "cursor-copy border-[#166B93] bg-[#166B93]/5 shadow-[0_6px_16px_rgba(22,107,147,0.08)]"
          : "cursor-pointer border-gray-300 hover:border-[#166B93]/50 hover:bg-gray-50"
      }`}
    >
      <Upload className="mx-auto mb-2 h-7 w-7 text-gray-400" strokeWidth={1.75} />
      <p className="text-sm font-medium text-gray-600">
        <Trans>Drag & drop files here, or</Trans>{" "}
        <span className="text-[#166B93] underline">
          <Trans>browse</Trans>
        </span>
      </p>
      <p className="text-xs text-gray-400 mt-1">
        <Trans>PDF, JPG, PNG, DICOM accepted</Trans>
      </p>
      {showCameraCapture && onTakePhoto && (
        <div className="mt-7">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onTakePhoto();
            }}
            className="!m-0 inline-flex h-10 items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#166B93] to-[#1A7FAF] px-3.5 text-sm font-semibold text-white shadow-[0_8px_18px_rgba(22,107,147,0.32)] transition-all hover:-translate-y-0.5 hover:from-[#135A7C] hover:to-[#166B93] hover:shadow-[0_10px_22px_rgba(22,107,147,0.4)] active:translate-y-0"
          >
            <Camera className="h-4 w-4" />
            <Trans>Take photo</Trans>
          </button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.dcm,.tif,.tiff"
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          if (picked.length) onFilesAdded(picked);
          e.target.value = "";
        }}
      />
    </div>
  );
}

function CombinedDocumentsUploadSection({
  uploads,
  onFilesAdded,
  onTakePhoto,
  getItemKey,
  uploadProgress,
  uploadErrors,
  isItemUploading,
  isItemRemoving,
  onRemoveItem,
}: {
  uploads: IntakeData["uploads"];
  onFilesAdded: (files: File[]) => void;
  onTakePhoto: () => void;
  getItemKey: (item: File | UploadedMedia) => string;
  uploadProgress: Record<string, number>;
  uploadErrors: Record<string, string>;
  isItemUploading: (item: File | UploadedMedia) => boolean;
  isItemRemoving: (item: File | UploadedMedia) => boolean;
  onRemoveItem: (category: UploadCategory, idx: number) => Promise<void>;
}) {
  const labFiles = uploads.labsImaging ?? [];
  const photoFiles = uploads.photos ?? [];

  const addFiles = (newFiles: File[]) => {
    onFilesAdded(newFiles);
  };

  const hasFiles = labFiles.length > 0 || photoFiles.length > 0;

  return (
    <section className="max-w-lg">
      <p className="text-xs text-gray-500 mb-3 leading-relaxed">
        <Trans>
          Blood tests, MRI, X-rays, or other medical reports, and optional
          photos that help us understand your condition.
        </Trans>
      </p>
      <UploadDropZone
        onFilesAdded={addFiles}
        showCameraCapture
        onTakePhoto={onTakePhoto}
      />
      {hasFiles && (
        <ul className="mt-3 space-y-2 [&_button]:!m-0">
          {labFiles.map((f, i) => (
            <UploadFileRow
              key={`labsImaging-${i}-${getUploadItemName(f)}`}
              file={f}
              onRemove={() => {
                void onRemoveItem("labsImaging", i);
              }}
              uploadProgress={uploadProgress[getItemKey(f)]}
              isUploading={isItemUploading(f)}
              uploadError={uploadErrors[getItemKey(f)]}
              isRemoving={isItemRemoving(f)}
            />
          ))}
          {photoFiles.map((f, i) => (
            <UploadFileRow
              key={`photos-${i}-${getUploadItemName(f)}`}
              file={f}
              onRemove={() => {
                void onRemoveItem("photos", i);
              }}
              uploadProgress={uploadProgress[getItemKey(f)]}
              isUploading={isItemUploading(f)}
              uploadError={uploadErrors[getItemKey(f)]}
              isRemoving={isItemRemoving(f)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function UploadSection({
  title,
  hint,
  category,
  uploads,
  onFilesAdded,
  showCameraCapture = false,
  onTakePhoto,
  getItemKey,
  uploadProgress,
  uploadErrors,
  isItemUploading,
  isItemRemoving,
  onRemoveItem,
}: {
  title: ReactNode;
  hint?: ReactNode;
  category: UploadCategory;
  uploads: IntakeData["uploads"];
  onFilesAdded: (category: UploadCategory, files: File[]) => void;
  showCameraCapture?: boolean;
  onTakePhoto?: () => void;
  getItemKey: (item: File | UploadedMedia) => string;
  uploadProgress: Record<string, number>;
  uploadErrors: Record<string, string>;
  isItemUploading: (item: File | UploadedMedia) => boolean;
  isItemRemoving: (item: File | UploadedMedia) => boolean;
  onRemoveItem: (category: UploadCategory, idx: number) => Promise<void>;
}) {
  const files = uploads[category] ?? [];

  const addFiles = (newFiles: File[]) => {
    onFilesAdded(category, newFiles);
  };

  return (
    <section className="max-w-lg">
      <h3 className="text-[13px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
        {title}
      </h3>
      {hint && (
        <p className="text-xs text-gray-500 mb-3 leading-relaxed">{hint}</p>
      )}
      <UploadDropZone
        onFilesAdded={addFiles}
        showCameraCapture={showCameraCapture}
        onTakePhoto={onTakePhoto}
      />
      {files.length > 0 && (
        <ul className="mt-3 space-y-2 [&_button]:!m-0">
          {files.map((f, i) => (
            <UploadFileRow
              key={`${category}-${i}-${getUploadItemName(f)}`}
              file={f}
              onRemove={() => {
                void onRemoveItem(category, i);
              }}
              uploadProgress={uploadProgress[getItemKey(f)]}
              isUploading={isItemUploading(f)}
              uploadError={uploadErrors[getItemKey(f)]}
              isRemoving={isItemRemoving(f)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

interface DocumentsCombinedCardProps {
  card: CardDef;
  formUid?: string;
  uploads: IntakeData["uploads"];
  onUploads: (uploads: IntakeData["uploads"]) => void;
  links: DocumentReferenceLink[];
  onLinks: (v: DocumentReferenceLink[]) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip?: () => void;
  index: number;
}

export function DocumentsCombinedCard({
  card,
  formUid,
  uploads,
  onUploads,
  links,
  onLinks,
  onNext,
  onBack,
  onSkip,
  index,
}: DocumentsCombinedCardProps) {
  const { _ } = useLingui();
  const cfg = card.documentsCombinedConfig ?? {
    showLabsImaging: true,
    showAdditionalPhotos: true,
    showDocumentLinks: true,
  };

  const [draft, setDraft] = useState("");
  const [showCamera, setShowCamera] = useState(false);
  const [uploadingKeys, setUploadingKeys] = useState<Set<string>>(new Set());
  const [removingKeys, setRemovingKeys] = useState<Set<string>>(new Set());
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  const [uploadError, setUploadError] = useState<string | null>(null);
  const localFileKeysRef = useRef(new WeakMap<File, string>());
  const uploadsRef = useRef(uploads);

  useEffect(() => {
    uploadsRef.current = uploads;
  }, [uploads]);

  const getItemKey = (item: File | UploadedMedia): string => {
    if (isUploadedMedia(item)) return `uploaded:${item.id}`;
    const mapped = localFileKeysRef.current.get(item);
    if (mapped) return mapped;
    const nextKey = `local:${item.name}:${item.size}:${item.lastModified}:${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    localFileKeysRef.current.set(item, nextKey);
    return nextKey;
  };

  const isItemUploading = (item: File | UploadedMedia) =>
    uploadingKeys.has(getItemKey(item));
  const isItemRemoving = (item: File | UploadedMedia) =>
    removingKeys.has(getItemKey(item));

  const uploadSingleFile = async (category: UploadCategory, file: File) => {
    if (!formUid) return;
    const itemKey = getItemKey(file);
    setUploadErrors((prev) => {
      const next = { ...prev };
      delete next[itemKey];
      return next;
    });
    setUploadingKeys((prev) => new Set(prev).add(itemKey));
    setUploadProgress((prev) => ({ ...prev, [itemKey]: 5 }));

    try {
      const presignResponse = await patientIntakeApi.getPhotoUploadUrl(
        formUid,
        file.type,
        file.size,
        file.name,
      );
      setUploadProgress((prev) => ({ ...prev, [itemKey]: 45 }));
      await patientIntakeApi.uploadPhotoToCloud(
        presignResponse.data.uploadUrl,
        file,
      );
      setUploadProgress((prev) => ({ ...prev, [itemKey]: 80 }));
      const registerResponse = await patientIntakeApi.registerPhoto(
        formUid,
        presignResponse.data.bucketName,
        presignResponse.data.storagePath,
        file.name,
        file.type,
        file.size,
        category,
      );
      const media = registerResponse.data?.media;
      if (!media) {
        throw new Error(_(msg`Upload completed but media registration failed.`));
      }
      setUploadProgress((prev) => ({ ...prev, [itemKey]: 100 }));

      const uploadedItem: UploadedMedia = {
        id: media.id,
        filename: file.name,
        mimeType: file.type,
        size: file.size,
        downloadUrl: media.downloadUrl || "",
        mediaType: media.mediaType || "photo",
        status: media.status,
        isUploaded: true,
      };

      const current = uploadsRef.current;
      const categoryItems = [...(current[category] ?? [])];
      const itemIndex = categoryItems.findIndex((item) => item === file);
      if (itemIndex === -1) {
        // User removed the file before upload completed; clean up remote media.
        try {
          await patientIntakeApi.deleteMedia(uploadedItem.id);
        } catch {
          // Best effort cleanup.
        }
        return;
      }
      categoryItems[itemIndex] = uploadedItem;
      onUploads({ ...current, [category]: categoryItems });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : _(msg`Failed to upload file. Please try again.`);
      setUploadErrors((prev) => ({ ...prev, [itemKey]: message }));
      setUploadError(message);
    } finally {
      setUploadingKeys((prev) => {
        const next = new Set(prev);
        next.delete(itemKey);
        return next;
      });
    }
  };

  const handleCapturedPhoto = (imageData: string) => {
    try {
      const base64 = imageData.split(",")[1];
      if (!base64) return;
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: "image/jpeg" });
      const file = new File(
        [blob],
        `camera-photo-${Date.now()}.jpg`,
        { type: "image/jpeg", lastModified: Date.now() },
      );
      const current = uploadsRef.current;
      const existing = current.photos ?? [];
      onUploads({ ...current, photos: [...existing, file] });
      void uploadSingleFile("photos", file);
    } finally {
      setShowCamera(false);
    }
  };

  const addLink = () => {
    const url = draft.trim();
    if (!url) return;
    try {
      new URL(url.startsWith("http") ? url : `https://${url}`);
    } catch {
      return;
    }
    onLinks([
      ...links,
      { url: url.startsWith("http") ? url : `https://${url}` },
    ]);
    setDraft("");
  };

  const removeLink = (idx: number) => {
    onLinks(links.filter((_, i) => i !== idx));
  };

  const handleFilesAdded = (newFiles: File[]) => {
    setUploadError(null);
    setUploadErrors({});
    const current = uploadsRef.current;
    const nextUploads: IntakeData["uploads"] = {
      ...current,
      labsImaging: [...(current.labsImaging ?? [])],
      photos: [...(current.photos ?? [])],
    };
    const queue: Array<{ category: UploadCategory; file: File }> = [];
    for (const file of newFiles) {
      const category = routeFileToUploadCategory(file);
      if (category === "labsImaging") {
        nextUploads.labsImaging = [...(nextUploads.labsImaging ?? []), file];
      } else {
        nextUploads.photos = [...(nextUploads.photos ?? []), file];
      }
      queue.push({ category, file });
    }
    onUploads(nextUploads);
    queue.forEach(({ category, file }) => {
      void uploadSingleFile(category, file);
    });
  };

  const handleFilesAddedForCategory = (
    category: UploadCategory,
    newFiles: File[],
  ) => {
    setUploadError(null);
    setUploadErrors({});
    const current = uploadsRef.current;
    const nextItems = [...(current[category] ?? []), ...newFiles];
    onUploads({ ...current, [category]: nextItems });
    newFiles.forEach((file) => {
      void uploadSingleFile(category, file);
    });
  };

  const handleRemoveItem = async (category: UploadCategory, idx: number) => {
    const current = uploadsRef.current;
    const items = [...(current[category] ?? [])];
    const item = items[idx];
    if (!item) return;
    const itemKey = getItemKey(item);
    setRemovingKeys((prev) => new Set(prev).add(itemKey));

    try {
      if (isUploadedMedia(item)) {
        try {
          await patientIntakeApi.deleteMedia(item.id);
        } catch {
          // Keep UX responsive even if backend delete fails.
        }
      }

      items.splice(idx, 1);
      onUploads({ ...current, [category]: items });
    } finally {
      setRemovingKeys((prev) => {
        const next = new Set(prev);
        next.delete(itemKey);
        return next;
      });
    }
  };

  const handleContinue = async () => {
    setUploadError(null);
    if (uploadingKeys.size > 0) {
      setUploadError(_(msg`Please wait for uploads to finish before continuing.`));
      return;
    }
    onNext();
  };

  useIntakeMobileChromeRegistration(
    () => ({
      onContinue: handleContinue,
      onCancel: onBack,
      onSkip,
      continueLoading: uploadingKeys.size > 0,
    }),
    [handleContinue, onBack, onSkip, uploadingKeys.size],
  );

  return (
    <div className="max-w-lg">
      <CardQuestion question={card.question} hint={card.hint} index={index} />

      <div className="space-y-5 sm:space-y-6">
        {cfg.showLabsImaging && cfg.showAdditionalPhotos ? (
          <CombinedDocumentsUploadSection
            uploads={uploads}
            onFilesAdded={handleFilesAdded}
            onTakePhoto={() => setShowCamera(true)}
            getItemKey={getItemKey}
            uploadProgress={uploadProgress}
            uploadErrors={uploadErrors}
            isItemUploading={isItemUploading}
            isItemRemoving={isItemRemoving}
            onRemoveItem={handleRemoveItem}
          />
        ) : (
          <>
            {cfg.showLabsImaging && (
              <UploadSection
                title={<Trans>Lab results & imaging</Trans>}
                hint={
                  <Trans>
                    Blood tests, MRI, X-rays, or other medical reports.
                  </Trans>
                }
                category="labsImaging"
                uploads={uploads}
                onFilesAdded={(category, files) => {
                  if (category === "labsImaging") {
                    handleFilesAddedForCategory("labsImaging", files);
                  }
                }}
                getItemKey={getItemKey}
                uploadProgress={uploadProgress}
                uploadErrors={uploadErrors}
                isItemUploading={isItemUploading}
                isItemRemoving={isItemRemoving}
                onRemoveItem={handleRemoveItem}
              />
            )}

            {cfg.showAdditionalPhotos && (
              <UploadSection
                title={<Trans>Additional photos</Trans>}
                hint={
                  <Trans>
                    Photos that help us understand your condition (optional).
                  </Trans>
                }
                category="photos"
                uploads={uploads}
                onFilesAdded={(category, files) => {
                  if (category === "photos") {
                    handleFilesAddedForCategory("photos", files);
                  }
                }}
                showCameraCapture
                onTakePhoto={() => setShowCamera(true)}
                getItemKey={getItemKey}
                uploadProgress={uploadProgress}
                uploadErrors={uploadErrors}
                isItemUploading={isItemUploading}
                isItemRemoving={isItemRemoving}
                onRemoveItem={handleRemoveItem}
              />
            )}
          </>
        )}

        {cfg.showDocumentLinks && (
          <section className="max-w-lg">
            <h3 className="text-[13px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
              <Trans>Document links</Trans>
            </h3>
            <p className="text-xs text-gray-500 mb-3 leading-relaxed">
              <Trans>
                Paste links to Google Drive, Dropbox, or other cloud files.
              </Trans>
            </p>
            <div className="space-y-3 [&_button]:!m-0">
              {links.map((link, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                >
                  <Link className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="flex-1 text-sm text-[#166B93] truncate">
                    {link.url}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeLink(i)}
                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-transparent text-gray-400 transition-colors hover:border-red-100 hover:bg-red-50 hover:text-red-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-200"
                    aria-label={_(msg`Remove link`)}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-2 sm:gap-3">
                <input
                  type="url"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addLink();
                    }
                  }}
                  placeholder={_(msg`https://drive.google.com/...`)}
                  className="flex-1 min-w-0 bg-white/80 backdrop-blur-sm border-[1px] rounded-xl px-4 py-3 text-[17px] text-gray-900 placeholder-gray-400 border-gray-300 transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.02)] focus:outline-none focus:ring-0 focus:border-[#166B93]"
                />
                <button
                  type="button"
                  onClick={addLink}
                  className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#166B93] text-white flex items-center justify-center hover:bg-[#105373] transition-colors shadow-md !m-0"
                >
                  <Plus className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>
          </section>
        )}
      </div>

      {uploadError && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{uploadError}</p>
        </div>
      )}

      <CardActions
        onNext={handleContinue}
        onBack={onBack}
        onSkip={onSkip}
        nextLoading={uploadingKeys.size > 0}
      />

      {showCamera && (
        <ModernCameraCapture
          onCapture={handleCapturedPhoto}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
}
